import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "./api";

const AuthContext = createContext(null);

const TOKEN_KEY = "ebel_token";
const ROLE_KEY = "ebel_role";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [role, setRole] = useState(() => localStorage.getItem(ROLE_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setRole(res.data.role);
        localStorage.setItem(ROLE_KEY, res.data.role);
      })
      .catch(() => {
        setToken(null);
        setRole(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ROLE_KEY);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (password) => {
    const res = await api.post("/auth/login", { password });
    localStorage.setItem(TOKEN_KEY, res.data.token);
    localStorage.setItem(ROLE_KEY, res.data.role);
    setToken(res.data.token);
    setRole(res.data.role);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    setToken(null);
    setRole(null);
  }, []);

  const value = useMemo(
    () => ({ token, role, loading, login, logout, isAdmin: role === "admin" }),
    [token, role, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children, adminOnly = false }) {
  const { token, role, loading } = useAuth();
  const location = useLocation();
  const navState = useMemo(() => ({ from: location }), [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4A4F46]">
        <span className="font-serif text-2xl">Einen Moment …</span>
      </div>
    );
  }
  if (!token) return <Navigate to="/login" state={navState} replace />;
  if (adminOnly && role !== "admin") return <Navigate to="/" replace />;
  return children;
}
